/**
 * Error Boundary Component
 * Catches JavaScript errors in component tree and displays fallback UI
 * Production-grade error handling
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from 'antd';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to error reporting service (e.g., Sentry, LogRocket)
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // You can also log to an error reporting service here
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI - Simple div-based UI to avoid icon errors
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#fff',
          }}
        >
          <div
            style={{
              fontSize: '48px',
              marginBottom: '16px',
              color: '#ff4d4f',
            }}
          >
            ⚠️
          </div>
          <h2 style={{ fontSize: '24px', marginBottom: '16px', color: '#262626' }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: '14px', marginBottom: '24px', color: '#8c8c8c', maxWidth: '500px' }}>
            {process.env.NODE_ENV === 'development' && this.state.error
              ? this.state.error.toString()
              : 'Please refresh the page or contact support if the problem persists.'}
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              type="primary"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
            <Button onClick={this.handleReset}>
              Try Again
            </Button>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <div
              style={{
                marginTop: '32px',
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                maxHeight: '400px',
                overflow: 'auto',
                maxWidth: '800px',
                width: '100%',
              }}
            >
              <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {this.state.errorInfo.componentStack}
              </pre>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

